import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './PropertyCashbackOfferBanner.css';

const CASHBACK_BANNER_SHOWN_KEY = '360coordinates_cashback_banner_shown';
const SELLER_PROPERTIES_PATH = '/seller-dashboard/properties';
const AGENT_PROPERTIES_PATH = '/agent-dashboard/properties';

const SPARKLE_POSITIONS = [
  { top: '8%', left: '12%', delay: '0s' },
  { top: '14%', right: '18%', delay: '0.4s' },
  { top: '42%', left: '6%', delay: '0.8s' },
  { top: '55%', right: '8%', delay: '0.2s' },
  { top: '72%', left: '20%', delay: '1s' },
  { top: '78%', right: '22%', delay: '0.6s' },
];

const PropertyCashbackOfferBanner = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsAnimating(false);
      return undefined;
    }

    const frame = requestAnimationFrame(() => setIsAnimating(true));
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  const handleDismiss = useCallback(() => {
    setIsAnimating(false);
    sessionStorage.setItem(CASHBACK_BANNER_SHOWN_KEY, 'true');
    setTimeout(() => {
      onClose();
    }, 220);
  }, [onClose]);

  const handleAddProperty = useCallback(() => {
    sessionStorage.setItem(CASHBACK_BANNER_SHOWN_KEY, 'true');
    onClose();

    const userType = user?.user_type;

    if (!isAuthenticated) {
      navigate(
        `/login?role=seller&returnUrl=${encodeURIComponent(SELLER_PROPERTIES_PATH)}`
      );
      return;
    }

    if (userType === 'seller') {
      navigate(SELLER_PROPERTIES_PATH);
      return;
    }

    if (userType === 'agent') {
      navigate(AGENT_PROPERTIES_PATH);
      return;
    }

    navigate(
      `/login?role=seller&returnUrl=${encodeURIComponent(SELLER_PROPERTIES_PATH)}`
    );
  }, [isAuthenticated, navigate, onClose, user?.user_type]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleDismiss();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleDismiss]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`cashback-offer-overlay ${isAnimating ? 'cashback-offer-visible' : 'cashback-offer-hidden'}`}
      onClick={handleDismiss}
      role="presentation"
    >
      <div
        className="cashback-offer-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cashback-offer-title"
      >
        <div className="cashback-offer-sparkles" aria-hidden="true">
          {SPARKLE_POSITIONS.map((pos, index) => (
            <span
              key={index}
              className="cashback-offer-sparkle"
              style={{
                top: pos.top,
                left: pos.left,
                right: pos.right,
                animationDelay: pos.delay,
              }}
            />
          ))}
        </div>

        <button
          type="button"
          className="cashback-offer-close"
          onClick={handleDismiss}
          aria-label="Close offer"
        >
          ×
        </button>

        <div className="cashback-offer-ribbon">Limited Offer</div>

        <div className="cashback-offer-badge-wrap">
          <div className="cashback-offer-badge">
            <span className="cashback-offer-badge-amount">₹ 10</span>
          </div>
          <span className="cashback-offer-badge-label"> Cash</span>
        </div>

        <h2 id="cashback-offer-title" className="cashback-offer-title">
          Add your property &amp; earn cash
        </h2>

        <p className="cashback-offer-message">
          Add your property and get <strong>₹ 10 cash</strong> on property post
        </p>

        <ul className="cashback-offer-perks">
          <li>Quick listing in minutes</li>
          <li>Reach genuine buyers &amp; tenants</li>
          <li>Cash on every property post</li>
        </ul>

        <div className="cashback-offer-actions">
          <button
            type="button"
            className="cashback-offer-btn cashback-offer-btn--primary"
            onClick={handleAddProperty}
          >
            Add Property Now
          </button>
          <button
            type="button"
            className="cashback-offer-btn cashback-offer-btn--ghost"
            onClick={handleDismiss}
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

export const hasCashbackBannerBeenShown = () =>
  sessionStorage.getItem(CASHBACK_BANNER_SHOWN_KEY) === 'true';

export default PropertyCashbackOfferBanner;
